import * as React from 'react';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';
import * as moment from 'moment-timezone';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { OptionsViewModel } from '@models/common.models';
import { autoAuthoService, SetTripStatusRequestViewModel } from '@services/AutoAuthoService';

import { AutoAuthoStatusUpdateState } from './status-update.reducer';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum AutoAuthoStatusUpdateActionType {
  GetTripReasonsFailure = 'GetTripReasonsFailure',
  GetTripReasonsSuccess = 'GetTripReasonsSuccess',
  GetTripReasonsStart = 'GetTripReasonsStart',
  Reset = 'Reset',
  SelectSwoTripStatus = 'SelectSwoTripStatus',
  SetDate = 'SetDate',
  SetSetTripStatusFormDispatcher = 'SetSetTripStatusFormDispatcher',
  SetTripStatusFailure = 'SetTripStatusFailure',
  SetTripStatusSuccess = 'SetTripStatusSuccess',
  SetTripStatusStart = 'SetTripStatusStart'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
interface AutoAuthoStatusUpdateAction extends Action {
  readonly type: AutoAuthoStatusUpdateActionType;
}

export interface GetTripReasonsFailureAction extends AutoAuthoStatusUpdateAction {}
export interface GetTripReasonsStartAction extends AutoAuthoStatusUpdateAction {}
export interface GetTripReasonsSuccessAction extends AutoAuthoStatusUpdateAction {
  reasons: OptionsViewModel[];
}

export interface ResetAction extends AutoAuthoStatusUpdateAction {}

export interface SelectSwoTripStatusAction extends AutoAuthoStatusUpdateAction {
  readonly status: number;
}

export interface SetDateAction extends AutoAuthoStatusUpdateAction {
  readonly date: moment.Moment | null;
}

export interface SetSetTripStatusFormDispatcherAction extends AutoAuthoStatusUpdateAction {
  dispatcher: Function;
}

export interface SetTripStatusFailureAction extends AutoAuthoStatusUpdateAction {
  message?: string;
}
export interface SetTripStatusSuccessAction extends AutoAuthoStatusUpdateAction {
  tripStatus: number;
}
export interface SetTripStatusStartAction extends AutoAuthoStatusUpdateAction {}

// Actions for each action type declared here.
// They should all return an action object.
export const getTripReasons = (phoneNumber: string, swoId: number) => {
  return async (dispatch: Dispatch<AutoAuthoStatusUpdateState>) => {
    dispatch(getTripReasonsStart());
    try {
      const results = await autoAuthoService.getSwoPortalTripReasons(phoneNumber, swoId);
      dispatch(getTripReasonsSuccess(results.results));
    } catch (e) {
      dispatch(getTripReasonsFailure());
    }
  };
};

export function getTripReasonsFailure(): GetTripReasonsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Error retrieving data from network. Please try again later.'} />,
    ToastOptions.Error
  );
  return {
    type: AutoAuthoStatusUpdateActionType.GetTripReasonsFailure
  };
}

export function getTripReasonsSuccess(reasons: OptionsViewModel[]): GetTripReasonsSuccessAction {
  return {
    type: AutoAuthoStatusUpdateActionType.GetTripReasonsSuccess,
    reasons: reasons
  };
}

export function getTripReasonsStart(): GetTripReasonsStartAction {
  return {
    type: AutoAuthoStatusUpdateActionType.GetTripReasonsStart
  };
}

export function reset(): ResetAction {
  return {
    type: AutoAuthoStatusUpdateActionType.Reset
  };
}

export function selectSwoTripStatus(status: number): SelectSwoTripStatusAction {
  return {
    type: AutoAuthoStatusUpdateActionType.SelectSwoTripStatus,
    status: status
  };
}

export function setAppointmentDate(date: moment.Moment | null): SetDateAction {
  return {
    type: AutoAuthoStatusUpdateActionType.SetDate,
    date: date
  };
}

export function setSetTripStatusFormDispatcher(dispatcher: Function): SetSetTripStatusFormDispatcherAction {
  return {
    type: AutoAuthoStatusUpdateActionType.SetSetTripStatusFormDispatcher,
    dispatcher: dispatcher
  };
}

export const setTripStatus = (phoneNumber: string, swoId: number, request: SetTripStatusRequestViewModel) => {
  return async (dispatch: Dispatch<AutoAuthoStatusUpdateState>) => {
    dispatch(setTripStatusStart());
    try {
      await autoAuthoService.setTripStatus(phoneNumber, swoId, request);
      dispatch(setTripStatusSuccess(request.tripStatus));
    } catch (e) {
      dispatch(setTripStatusFailure('Error retrieving data from network. Please try again later.'));
    }
  };
};

export function setTripStatusFailure(message?: string): SetTripStatusFailureAction {
  if (message) {
    toast.error(<ToastComponent type={ToastType.Error} message={message} />, ToastOptions.Error);
  }
  return {
    type: AutoAuthoStatusUpdateActionType.SetTripStatusFailure,
    message: message
  };
}

export function setTripStatusSuccess(tripStatus: number): SetTripStatusSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`SWO Status successfully updated`} />,
    ToastOptions.Success
  );
  return {
    type: AutoAuthoStatusUpdateActionType.SetTripStatusSuccess,
    tripStatus: tripStatus
  };
}

export function setTripStatusStart(): SetTripStatusStartAction {
  return {
    type: AutoAuthoStatusUpdateActionType.SetTripStatusStart
  };
}



// WEBPACK FOOTER //
// ./src/auto-autho/_components/status-update/status-update.actions.tsx